import React from "react";
import { useTheme } from "react-jss";
import useStyles from "./NumericRankingRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import { isNullOrEmpty } from "../../../utils/index";
import { RecalledText } from "../../../containers/shared/RecalledText";
import { translate } from "../../../models/utilities/translationUtilities";
import { NumericRankingModel } from "../../../models/NumericRankingModel";
import { getChoiceRanking } from "../../../models/utilities/choiceUtilities";
import InputBox from "../../shared/InputBox";

interface Props extends RendererProps {
    rankings: NumericRankingModel[];
    labelsHeader: string;
    answerHeader: string;
    choices: ChoiceModel[];
    onFocus?: React.FocusEventHandler<Element>;
    onChange(choice: ChoiceModel, value: string): void;
}

function RankingHeader(props: Props) {
    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const { question } = props;
    const {
        answerHeader = getSettingString(question, "AnswersHeader"),
        labelsHeader = getSettingString(question, "LabelsHeader"),
    } = props;

    return (
        <tr className={classes.numericOption}>
            {theme.DisplayChoiceIcon === "right" && !isNullOrEmpty(labelsHeader) && (
                <td className={classes.numericOptionChoiceText}>
                    <RecalledText
                        text={labelsHeader}
                        render={(recalled) => (
                            <span
                                className={classes.numericOptionChoiceTextHeader}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </td>
            )}
            {!isNullOrEmpty(answerHeader) && (
                <td className={classes.numericOptionChoiceRanking}>
                    <RecalledText
                        text={answerHeader}
                        render={(recalled) => (
                            <span
                                className={classes.numericOptionChoiceRankingHeader}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </td>
            )}
            {theme.DisplayChoiceIcon !== "right" && !isNullOrEmpty(labelsHeader) && (
                <td className={classes.numericOptionChoiceText}>
                    <RecalledText
                        text={labelsHeader}
                        render={(recalled) => (
                            <span
                                className={classes.numericOptionChoiceTextHeader}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </td>
            )}
        </tr>
    );
}

function Header({ language, choice }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    return (
        <tr className={classes.numericOption}>
            <RecalledText
                text={translate(choice, "TEXT", language)}
                render={(recalled) => (
                    <td
                        className={classes.numericOptionChoiceHeader}
                        dangerouslySetInnerHTML={{ __html: recalled }}
                        colSpan={2}
                    />
                )}
            />
        </tr>
    );
}

function Row(props: Props & { choice: ChoiceModel }) {
    const [focused, setFocused] = React.useState(false);
    const theme: any = useTheme();
    const classes = useStyles({ theme });
    const { choice, rankings, language, onFocus, onChange } = props;
    const choiceText = translate(choice, "TEXT", language);

    return (
        <tr className={classes.numericOption}>
            {theme.DisplayChoiceIcon === "right" && (
                <td className={classes.numericOptionChoiceText}>
                    <RecalledText
                        text={choiceText}
                        render={(recalled) => (
                            <label
                                id={`${choice.elementId}_LABEL`}
                                htmlFor={`${choice.elementId}_INPUT`}
                                className={classes.numericOptionChoiceLabel}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </td>
            )}
            <td className={classes.numericOptionChoiceRanking}>
                <InputBox
                    focused={focused}
                    className={classes.numericOptionChoiceInput}
                    size="XSmall"
                >
                    <input
                        id={`${choice.elementId}_INPUT`}
                        type="text"
                        inputMode="decimal"
                        value={getChoiceRanking(choice, rankings)}
                        onFocus={(e) => {
                            setFocused(true);
                            onFocus(e);
                        }}
                        onBlur={() => setFocused(false)}
                        onChange={(e) => onChange(choice, (e.target as any).value)}
                    />
                </InputBox>
            </td>
            {theme.DisplayChoiceIcon !== "right" && (
                <td className={classes.numericOptionChoiceText}>
                    <RecalledText
                        text={translate(choice, "TEXT", language)}
                        render={(recalled) => (
                            <label
                                id={`${choice.elementId}_LABEL`}
                                htmlFor={`${choice.elementId}_INPUT`}
                                className={classes.numericOptionChoiceLabel}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </td>
            )}
        </tr>
    );
}

export default function NumericRankingRenderer(props: Props) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const { choices, language } = props;
    return (
        <div className={classes.numericRanking}>
            <table className={classes.numericOptionList}>
                <tbody>
                    <RankingHeader {...props} />
                    {choices.map((choice) =>
                        !choice.value ? (
                            <Header key={choice.elementId} language={language} choice={choice} />
                        ) : (
                            <Row key={choice.elementId} {...props} choice={choice} />
                        ),
                    )}
                </tbody>
            </table>
            <div className="clear" />
        </div>
    );
}
